"use client"

import { useQuery, useQueryClient } from "@tanstack/react-query"

import { accountGateway } from "@/platform/api/gateways/account"
import type { BrowserSessionStatus, StoreCustomer } from "@/platform/api/session-contract"
import { probeSession } from "@/platform/session/probe"
import { observeSession } from "@/platform/session/publisher"
import type { Revocation } from "@/platform/session/revocation"
import { sessionKeys } from "@/platform/session/session-keys"
import { useApi, useGateway } from "@/platform/session/use-api"
import { usePrivateQuery } from "@/platform/session/use-private-query"
import { useRevocation } from "@/platform/session/use-revocation"

export interface Session {
  revocation: Revocation
  status: BrowserSessionStatus
  customer: StoreCustomer | null
  customerFailed: boolean
  retryCustomer: () => void
  isLoading: boolean
  isAuthenticated: boolean
}

const PROBE_STALE_TIME_MS = 60_000

export const useSessionProbe = (enabled = true) => {
  const queryClient = useQueryClient()
  const revocation = useRevocation(queryClient)
  const api = useApi()
  const probe = useQuery({
    queryKey: sessionKeys.probe,
    queryFn: ({ signal }): Promise<BrowserSessionStatus> => observeSession(queryClient, () => probeSession(api, signal)),
    enabled: enabled && revocation === "none",
    staleTime: PROBE_STALE_TIME_MS,
    retry: false,
  })
  return { probe, revocation }
}

export const useSession = (enabled = true): Session => {
  const { probe, revocation } = useSessionProbe(enabled)
  const account = useGateway(accountGateway)

  const known = probe.data?.kind === "authenticated" || probe.data?.kind === "anonymous" ? probe.data : undefined
  const status: BrowserSessionStatus = known ?? (probe.isError ? { kind: "unavailable" } : { kind: "loading" })
  const userUuid = status.kind === "authenticated" ? status.userUuid : null
  const customer = usePrivateQuery({
    queryKey: sessionKeys.customer,
    queryFn: ({ signal }) => account.customer(signal),
    enabled: enabled && userUuid !== null,
    staleTime: PROBE_STALE_TIME_MS,
    retry: false,
  })

  return {
    revocation,
    status,
    customer: userUuid && !customer.isError ? (customer.data ?? null) : null,
    customerFailed: userUuid !== null && customer.isError,
    retryCustomer: () => void customer.refetch(),
    isLoading: status.kind === "loading" || (userUuid !== null && customer.isPending),
    isAuthenticated: status.kind === "authenticated",
  }
}
